import * as THREE from 'three';
import Stats from 'three/examples/jsm/libs/stats.module';
import { GUI } from 'three/examples/jsm/libs/lil-gui.module.min.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';


export class TreeLampScene {

    #scene: THREE.Scene;
    #camera: THREE.PerspectiveCamera;
    #renderer: THREE.WebGLRenderer;
    #controls: OrbitControls;
    #stats: Stats;

    #settings: {
        exposure: number;
    };

    constructor () {

        this.#scene = new THREE.Scene();

        this.#camera = new THREE.PerspectiveCamera(
            80,
            window.innerWidth / window.innerHeight,
            0.01,
            1000
        );
        this.#camera.position.set(2.5, 1.60, 2.5);
        this.#camera.lookAt(new THREE.Vector3(0,0,0));

        this.#renderer = new THREE.WebGLRenderer(
            {
                antialias: true,
            }
        );
        this.#renderer.shadowMap.enabled = true;
        this.#renderer.shadowMap.type = THREE.PCFSoftShadowMap;

        this.#renderer.physicallyCorrectLights = true;

        this.#renderer.toneMapping = THREE.ReinhardToneMapping;

        this.#renderer.setSize(window.innerWidth, window.innerHeight);
        document.body.appendChild(this.#renderer.domElement);

        this.#stats = Stats();
        document.body.appendChild(this.#stats.dom);

        this.#controls = new OrbitControls(
            this.#camera,
            this.#renderer.domElement
        );

        this.#settings = {
            exposure: 1.00,
        }

        this.#addObjectsToScene();
        this.#addControls();
    }

    render () {

        this.#animate();

        window.addEventListener('resize', this.#handleResize.bind(this), false);
    }

    #addControls () {

        const gui = new GUI();

        gui.add( this.#settings, 'exposure', 0, 1 );

        gui.open();
    }

    #handleResize () {

        this.#camera.aspect = window.innerWidth / window.innerHeight;
        this.#camera.updateProjectionMatrix();
        this.#renderer.setSize(window.innerWidth, window.innerHeight);
    }

    #animate () {

        requestAnimationFrame(this.#animate.bind(this));

        this.#controls.update();

        this.#stats.update();

        this.#renderer.toneMappingExposure = Math.pow(this.#settings.exposure, 5.0);

        this.#renderer.render(this.#scene, this.#camera);
    }

    #addObjectsToScene () {

        this.#scene.add(this.#createRoom());
        this.#scene.add(this.#createAmbientLight());
        this.#scene.add(this.#createLight());
        this.#scene.add(this.#createGlassCylinder());
        this.#scene.add(this.#createBaseBox());
    }

    #createRoom () {

        const width = 10;
        const height = 4;
        const depth = 6;

        const geometry = new THREE.BoxGeometry(
            width,
            height,
            depth,
            1,
            1,
            1
        );

        const material = new THREE.MeshPhongMaterial(
            {
                side: THREE.BackSide,
                color: 0xa0a0a0,
                shininess: 0,
                specular: 0xffffff,
            }
        );

        const room = new THREE.Mesh(geometry, material);

        room.position.set(0, height / 2, 0)

        room.receiveShadow = true;

        return room;
    }

    #createAmbientLight () {

        const color = 0xffffff;
        const intensity = 0.5;

        return new THREE.AmbientLight(color, intensity);
    }

    #createLight () {

        const color = 0xfcffcf;
        const intensity = 1;
        const distance = 100;
        const decay = 2;
        const lumen = 800;

        const light = new THREE.PointLight(color, intensity, distance, decay);

        light.castShadow = true;
        light.shadow.bias = -0.0001;
        light.shadow.mapSize.width = 1024 * 2;
        light.shadow.mapSize.height = 1024 * 2;
        light.shadow.camera.near = 0.001;
        light.shadow.camera.far = 100;

        light.power = lumen;

        let material = new THREE.MeshStandardMaterial(
            {
                emissive: 0xffffee,
                emissiveIntensity: 1,
                color: 0x000000
            }
         );

        let geometry = new THREE.SphereGeometry(.035, 32, 32);

        let sphere = new THREE.Mesh( geometry, material );
        light.add( sphere );

        light.translateY(.125);

        return light;
    }

    #createBaseBox () {

        const generateWoodTexture = () => {
            const textureLoader = new THREE.TextureLoader();
            const texture = textureLoader.load('./wood.jpg');

            texture.magFilter = THREE.NearestFilter;
            texture.wrapT = THREE.RepeatWrapping;
            texture.wrapS = THREE.RepeatWrapping;
            texture.anisotropy = 16;

            return texture;
        }

        const material = new THREE.MeshPhongMaterial(
            {
                side: THREE.DoubleSide,
                map: generateWoodTexture(),
            }
        );

        const geometry = new THREE.BoxGeometry(.200, .050, .200);

        const baseBox = new THREE.Mesh(geometry, material);

        baseBox.castShadow = true;
        baseBox.receiveShadow = true;

        baseBox.translateY(.0270);

        return baseBox;
    }

    #createGlassCylinder () {

        const generateBlackTexture = () => {

            const canvas = document.createElement( 'canvas' );
            canvas.width = 2;
            canvas.height = 2;

            const context = canvas.getContext( '2d' );

            if (context !== null) {
                context.fillStyle = 'black';
                context.fillRect(0, 0, 1, 1);
            }

            const texture = new THREE.CanvasTexture(canvas);

            return texture;
        }

        const generateTreeTexture = () => {

            const textureLoader = new THREE.TextureLoader();
            const texture = textureLoader.load('./texture_lamp_shade_tree.png');

            texture.magFilter = THREE.NearestFilter;
            texture.wrapT = THREE.RepeatWrapping;
            texture.wrapS = THREE.RepeatWrapping;
            texture.anisotropy = 16;

            return texture;
        }

        const sideMaterial = new THREE.MeshPhongMaterial(
            {
                side: THREE.DoubleSide,
                alphaMap: generateTreeTexture(),
                alphaTest: 0.5,
            }
        );

        const topMaterial = new THREE.MeshPhongMaterial(
            {
                side: THREE.DoubleSide,
                alphaMap: generateBlackTexture(),
                alphaTest: 0.5,
            }
        );

        const bottomMaterial = new THREE.MeshPhongMaterial(
            {
                side: THREE.DoubleSide,
                alphaMap: generateBlackTexture(),
                alphaTest: 0.5,
            }
        );

        const geometry = new THREE.CylinderGeometry(.050, .050, .150, 64);

        const glassCylinder = new THREE.Mesh(
            geometry,
            [
                sideMaterial,
                topMaterial,
                bottomMaterial
            ]
        );

        glassCylinder.castShadow = true;
        glassCylinder.receiveShadow = true;

        glassCylinder.translateY(.125);

        const distanceMaterial = new THREE.MeshDistanceMaterial(
            {
                alphaMap: sideMaterial.alphaMap,
                alphaTest: sideMaterial.alphaTest
            }
        );
        glassCylinder.customDistanceMaterial = distanceMaterial;

        return glassCylinder;
    }
};
